import { useMemo } from 'react';

import { useLocale } from 'next-intl';

import { siteVersion, tinyVersion } from '@/global/public-settings';

export enum LEGAL_DOCS {
  DEPOSITS_WITHDRAWALS,
  AML_KYC,
  LEGAL_TERMS,
  RISK,
  PRIVACY,
  PAIA_PRIVACY,
  COOKIE,
  BAKSTA,
  PXBT,
  PARTNERS_PITCH_DECK,
  PARTNERS_PRIVACY,
  PARTNERS,
  COMPLAINTS,
  AML,
  COPY_TRADING,
  CRYPTO_FUTURES,
  WITHDRAWAL_AND_REFUND,
  PROMO_CODES_N_BONUS_USAGE,
  PROMOTION,
  REFERRAL,
  COMPETITIONS,
  CONFLICT_OF_INTEREST,
  WELCOME_BONUS,
  REWARDS_CENTRE,
  FAIS_DISCLOSURE,
  CRYPTO_MARGIN,
  FIFTY_PERCENT_DEPOSIT_BONUS,
  PXBT_PARTNERS,
  PXBT_PARTNERS_PRIVACY,
  PXBT_CLIENT,
  PXBT_COMPLAINTS,
  PXBT_CONFIDENTIALITY,
  PXBT_INTEREST,
  PXBT_ORDER,
  PXBT_PRIVACY,
  PXBT_RISK,

  // HUNDRED_PERCENT_PROMOTION,
  // FIXED_BONUS,
  // HALLOWEEN,
  // EVENT_PROMOTION,
}

const africaDocsPath = '/assets/documents/pdf/primexbtZa';
const turkeyDocsPath = '/assets/documents/pdf/primexbtTurkey';
const mainDocsPath = '/assets/documents/pdf/primexbt';
const partnersDocsPath = '/assets/documents/pdf/partners';
const primexbtGlobalDocsPath = '/assets/documents/pdf/primexbtGlobal';

export function useLegalDocumentsLinks(): Record<
  'mainDocumentsLinks' | 'turkishDocumentsLinks' | 'africanDocumentsLinks',
  Partial<Record<LEGAL_DOCS, string>>
> {
  const locale = useLocale();

  // prettier-ignore
  const mainDocumentsLinks: Partial<Record<LEGAL_DOCS, string>> = useMemo(
    () => ({
      [LEGAL_DOCS.BAKSTA]: 'https://baksta.com/terms-and-conditions/',
      [LEGAL_DOCS.PXBT]: 'https://pxbt.com/legal-terms/',
      [LEGAL_DOCS.PXBT_PARTNERS]: `https://pxbt.com/assets/documents/pdf/pxbt/pxbt-affiliates-introducing-brokers-agreement.pdf`,
      [LEGAL_DOCS.PXBT_PARTNERS_PRIVACY]: `https://pxbt.com/assets/documents/pdf/pxbt/partners-privacy-policy.pdf`,
      [LEGAL_DOCS.PXBT_CLIENT]: `${primexbtGlobalDocsPath}/client-service-agreement.pdf`,
      [LEGAL_DOCS.PXBT_COMPLAINTS]: `${primexbtGlobalDocsPath}/complaints-handling-policy.pdf`,
      [LEGAL_DOCS.PXBT_CONFIDENTIALITY]: `${primexbtGlobalDocsPath}/confidentiality-policy.pdf`,
      [LEGAL_DOCS.PXBT_INTEREST]: `${primexbtGlobalDocsPath}/conflicts-of-interest-policy.pdf`,
      [LEGAL_DOCS.PXBT_ORDER]: `${primexbtGlobalDocsPath}/order-execution-policy.pdf`,
      [LEGAL_DOCS.PXBT_PRIVACY]: `${primexbtGlobalDocsPath}/privacy-policy.pdf`,
      [LEGAL_DOCS.PXBT_RISK]: `${primexbtGlobalDocsPath}/risk-disclosure-statement.pdf`,
      //
      [LEGAL_DOCS.LEGAL_TERMS]: `${mainDocsPath}/terms-and-conditions.pdf`,
      [LEGAL_DOCS.RISK]: `${mainDocsPath}/risk-disclosure-statement.pdf`,
      [LEGAL_DOCS.PRIVACY]: `${mainDocsPath}/privacy-policy.pdf`,
      [LEGAL_DOCS.COOKIE]: `${mainDocsPath}/cookie-policy.pdf`,
      [LEGAL_DOCS.AML]: `${mainDocsPath}/anti-money-laundering-policy.pdf`,
      [LEGAL_DOCS.COMPLAINTS]: `${mainDocsPath}/complaints-handling-procedure.pdf`,
      [LEGAL_DOCS.COPY_TRADING]: `${mainDocsPath}/copy-trading-terms-and-conditions.pdf`,
      [LEGAL_DOCS.CRYPTO_FUTURES]: `${mainDocsPath}/crypto-futures-services-terms-of-use.pdf`,
      [LEGAL_DOCS.WITHDRAWAL_AND_REFUND]: `${mainDocsPath}/withdrawal-and-refund-policy.pdf`,
      [LEGAL_DOCS.PROMO_CODES_N_BONUS_USAGE]: `${mainDocsPath}/primexbt_general_rules_of_promotion_codes_and_bonus_usage.pdf`,
      [LEGAL_DOCS.PROMOTION]: `${mainDocsPath}/percentage_tradeable_bonus_promotion_terms_and_conditions.pdf`,
      [LEGAL_DOCS.COMPETITIONS]: `${mainDocsPath}/general-competition-terms-and-conditions.pdf`,
      [LEGAL_DOCS.WELCOME_BONUS]: `${mainDocsPath}/welcome_bonus_terms_and_conditions.pdf`,
      [LEGAL_DOCS.REWARDS_CENTRE]: `${mainDocsPath}/primexbt_rewards_center_terms_and_conditions.pdf`,
      [LEGAL_DOCS.PARTNERS_PRIVACY]: `${partnersDocsPath}/partners-privacy-policy.pdf`,
      [LEGAL_DOCS.PARTNERS]: `${partnersDocsPath}/primexbt-affiliates-introducing-brokers-agreement.pdf`,
      [LEGAL_DOCS.CRYPTO_MARGIN]: `${mainDocsPath}/crypto-margin.pdf`,
      // [LEGAL_DOCS.HUNDRED_PERCENT_PROMOTION]: `${mainDocsPath}/primexbt_hundred_percent_promotion_code_and_bonus_usage.pdf`,
      [LEGAL_DOCS.PARTNERS_PITCH_DECK]:
      // [LEGAL_DOCS.FIXED_BONUS]: `${mainDocsPath}/primexbt_fixed_tradeable_bonus_promotion_terms_and_conditions.pdf`,
      // [LEGAL_DOCS.HALLOWEEN]: `${mainDocsPath}/official-competition-terms-and-conditions.pdf`,
      // [LEGAL_DOCS.REFERRAL]: `${mainDocsPath}/refer-a-friend-bonus-program-terms-and-conditions.pdf`,
      // [LEGAL_DOCS.EVENT_PROMOTION]: `${mainDocsPath}/primexbt_event_promotion_terms_and_conditions.pdf`,
      {
        vi: `${partnersDocsPath}/pitch-deck/vi.pdf`,
        en: `${partnersDocsPath}/pitch-deck/en.pdf`,
        ar: `${partnersDocsPath}/pitch-deck/ar.pdf`,
        cn: `${partnersDocsPath}/pitch-deck/cn.pdf`,
        hi: `${partnersDocsPath}/pitch-deck/hi.pdf`,
        pt: `${partnersDocsPath}/pitch-deck/pt.pdf`,
        th: `${partnersDocsPath}/pitch-deck/th.pdf`,
        tr: `${partnersDocsPath}/pitch-deck/tr.pdf`,
      }[locale] || `${partnersDocsPath}/pitch-deck/en.pdf`,
    }),
    [locale],
  );

  // prettier-ignore
  const turkishDocumentsLinks: Partial<Record<LEGAL_DOCS, string>> = {
    [LEGAL_DOCS.LEGAL_TERMS]: `${turkeyDocsPath}/terms-and-conditions.pdf`,
    [LEGAL_DOCS.COPY_TRADING]: `${turkeyDocsPath}/copy-trading-terms-and-conditions.pdf`,
    [LEGAL_DOCS.COMPETITIONS]: `${turkeyDocsPath}/general-competition-terms-and-conditions.pdf`,
    [LEGAL_DOCS.PROMOTION]: `${turkeyDocsPath}/percentage_tradeable_bonus_promotion_terms_and_conditions.pdf`,
    [LEGAL_DOCS.PROMO_CODES_N_BONUS_USAGE]: `${turkeyDocsPath}/primexbt_general_rules_of_promotion_codes_and_bonus_usage.pdf`,
    [LEGAL_DOCS.WELCOME_BONUS]: `${turkeyDocsPath}/welcome_bonus_terms_and_conditions.pdf`,
    [LEGAL_DOCS.CRYPTO_MARGIN]: `${mainDocsPath}/crypto-margin.pdf`,
    // [LEGAL_DOCS.FIXED_BONUS]: `${turkeyDocsPath}/primexbt_fixed_tradeable_bonus_promotion_terms_and_conditions.pdf`,
    // [LEGAL_DOCS.HALLOWEEN]: `${turkeyDocsPath}/official-competition-terms-and-conditions.pdf`,
    // [LEGAL_DOCS.REFERRAL]: `${turkeyDocsPath}/refer-a-friend-bonus-program-terms-and-conditions.pdf`,
    // [LEGAL_DOCS.EVENT_PROMOTION]: `${turkeyDocsPath}/primexbt_event_promotion_terms_and_conditions.pdf`,
  };

  // prettier-ignore
  const africanDocumentsLinks: Partial<Record<LEGAL_DOCS, string>> = {
    [LEGAL_DOCS.DEPOSITS_WITHDRAWALS]: `${africaDocsPath}/deposits-withdrawals-policy.pdf`,
    [LEGAL_DOCS.AML_KYC]: `${africaDocsPath}/aml-kyc-policy.pdf`,
    [LEGAL_DOCS.PRIVACY]: `${africaDocsPath}/privacy-policy.pdf`,
    [LEGAL_DOCS.PAIA_PRIVACY]: `${africaDocsPath}/privacy-policy.pdf`,
    [LEGAL_DOCS.COOKIE]: `${africaDocsPath}/cookie-policy.pdf`,
    [LEGAL_DOCS.RISK]: `${africaDocsPath}/risk-disclosure-statement.pdf`,
    [LEGAL_DOCS.CONFLICT_OF_INTEREST]: `${africaDocsPath}/conflict-of-interest.pdf`,
    [LEGAL_DOCS.LEGAL_TERMS]: `${africaDocsPath}/terms-and-conditions.pdf`,
    [LEGAL_DOCS.COMPLAINTS]: `${africaDocsPath}/complaints-handling-procedure.pdf`,
    [LEGAL_DOCS.COMPETITIONS]: `${africaDocsPath}/general-competition-terms-and-conditions.pdf`,
    [LEGAL_DOCS.PROMO_CODES_N_BONUS_USAGE]: `${africaDocsPath}/primexbt_general_rules_of_promotion_codes_and_bonus_usage.pdf`,
    [LEGAL_DOCS.REWARDS_CENTRE]: `${africaDocsPath}/primexbt_rewards_center_terms_and_conditions.pdf`,
    [LEGAL_DOCS.REFERRAL]: `${africaDocsPath}/refer-a-friend-bonus-program-terms-and-conditions.pdf`,
    [LEGAL_DOCS.WELCOME_BONUS]: `${africaDocsPath}/welcome_bonus_terms_and_conditions.pdf`,
    [LEGAL_DOCS.FAIS_DISCLOSURE]: `${africaDocsPath}/fais-disclosure-document.pdf`,
    [LEGAL_DOCS.CRYPTO_MARGIN]: `${africaDocsPath}/crypto-margin.pdf`,
    [LEGAL_DOCS.FIFTY_PERCENT_DEPOSIT_BONUS]: `${africaDocsPath}/fifty_percent_deposit_bonus_tc.pdf`,
  };

  return { mainDocumentsLinks, turkishDocumentsLinks, africanDocumentsLinks };
}

export type DocumentLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  doc: LEGAL_DOCS;
  explicitRealm?: 'africa' | 'main' | 'global';
};

export function DocumentLink({ children, doc, explicitRealm, ...props }: DocumentLinkProps) {
  const { mainDocumentsLinks, turkishDocumentsLinks, africanDocumentsLinks } = useLegalDocumentsLinks();

  let _siteVersion: Partial<typeof siteVersion> = siteVersion;

  if (explicitRealm === 'africa') {
    _siteVersion = { isAfrica: true };
  }

  if (explicitRealm === 'main') {
    _siteVersion = _siteVersion.isTurkey ? { isTurkey: true } : { isMain: true };
  }

  const documentUrl = _siteVersion.isMain
    ? mainDocumentsLinks[doc]
    : _siteVersion.isTurkey
      ? { ...mainDocumentsLinks, ...turkishDocumentsLinks }[doc]
      : // TODO ZA remove || _siteVersion.isWaitlist
        _siteVersion.isAfrica || _siteVersion.isWaitlist
        ? { ...mainDocumentsLinks, ...africanDocumentsLinks }[doc]
        : mainDocumentsLinks[doc];

  if (!documentUrl) {
    console.error(new Error(`No documentUrl found for document ${doc} with tinyVersion=${tinyVersion}`));
  }

  return (
    <a href={documentUrl} target={'_blank'} {...props}>
      {children}
    </a>
  );
}
